import { useEffect, useRef, useState, type FormEvent, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import { BrandMark } from '@/shared/ui';
import { isPortalApiError } from '../api/portalProblem';
import { bffBaseUrl } from '../api/portalApiClient';
import { useRequestOtp, useVerifyOtp } from './otpApi';

type Step = 'email' | 'code';

/**
 * Sign-in: a 2-step email -> one-time-code flow (requirements §3.2).
 *
 * UX pattern mirrors the CRM's own `LoginPage.tsx` forgot-password flow
 * structurally (ui-ux-consistency-critic, swarm review: the closest existing
 * precedent for exactly this 2-step email+code shape, a chrome-free centered
 * card, and a fixed non-enumerating message regardless of outcome) — but is
 * a fresh component, NOT a cross-import of that CRM-module file, which is
 * built on the CRM's own Cognito-coupled `apiClient` (frontend-security-
 * auditor + ui-ux-consistency-critic, swarm review).
 */
export function SignInPage(): ReactElement {
  const navigate = useNavigate();
  const requestOtp = useRequestOtp();
  const verifyOtp = useVerifyOtp();
  const mountedRef = useRef(true);

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [ack, setAck] = useState<string | null>(null);
  const [topError, setTopError] = useState<string | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleRequestOtp = (event: FormEvent): void => {
    event.preventDefault();
    setTopError(null);
    const trimmed = email.trim();
    if (!trimmed) {
      setTopError('Enter your email address.');
      return;
    }
    requestOtp.mutate(
      { email: trimmed },
      {
        onSuccess: (data) => {
          if (!mountedRef.current) return;
          setAck(data.detail);
          setStep('code');
        },
        onError: (error) => {
          if (!mountedRef.current) return;
          setTopError(
            isPortalApiError(error) && error.detail
              ? error.detail
              : 'Something went wrong. Please try again.',
          );
        },
      },
    );
  };

  const handleVerifyOtp = (event: FormEvent): void => {
    event.preventDefault();
    setTopError(null);
    const trimmedCode = code.trim();
    if (!trimmedCode) {
      setTopError('Enter the code we sent you.');
      return;
    }
    verifyOtp.mutate(
      { email: email.trim(), code: trimmedCode },
      {
        onSuccess: () => {
          if (!mountedRef.current) return;
          navigate('/campaigns', { replace: true });
        },
        onError: (error) => {
          if (!mountedRef.current) return;
          setTopError(
            isPortalApiError(error) && error.detail
              ? error.detail
              : 'Something went wrong. Please try again.',
          );
        },
      },
    );
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        background: (theme) =>
          theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, #0f1215 0%, #1a2230 100%)'
            : 'linear-gradient(135deg, #eef2f7 0%, #ffffff 100%)',
      }}
    >
      <Paper
        elevation={3}
        sx={{ width: '100%', maxWidth: 420, p: { xs: 3, sm: 4 }, borderRadius: 2 }}
      >
        <Stack spacing={3} alignItems="center">
          <BrandMark />
          <Typography variant="h5" component="h1" textAlign="center">
            Partner Deals Portal
          </Typography>

          {topError ? (
            <Alert severity="error" sx={{ width: '100%' }}>
              {topError}
            </Alert>
          ) : null}

          {step === 'email' ? (
            <Box
              component="form"
              onSubmit={handleRequestOtp}
              sx={{ width: '100%' }}
              noValidate
            >
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  Enter your partner email address and we’ll send you a one-time code.
                </Typography>
                <TextField
                  label="Email address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  autoFocus
                  fullWidth
                  inputProps={{ 'data-testid': 'sign-in-email-input' }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={requestOtp.isPending}
                  sx={{ minHeight: 44 }}
                >
                  Send code
                </Button>
              </Stack>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleVerifyOtp} sx={{ width: '100%' }} noValidate>
              <Stack spacing={2}>
                {ack ? (
                  <Alert severity="info" sx={{ width: '100%' }} data-testid="otp-request-ack">
                    {ack}
                  </Alert>
                ) : null}
                <TextField
                  label="One-time code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  autoComplete="one-time-code"
                  autoFocus
                  fullWidth
                  inputProps={{ 'data-testid': 'sign-in-code-input' }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={verifyOtp.isPending}
                  sx={{ minHeight: 44 }}
                >
                  Verify &amp; sign in
                </Button>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => {
                    setStep('email');
                    setCode('');
                    setTopError(null);
                  }}
                  sx={{ minHeight: 44 }}
                >
                  Use a different email
                </Button>
              </Stack>
            </Box>
          )}

          <Button
            component="a"
            href={`${bffBaseUrl}/auth/staff/login`}
            variant="text"
            size="small"
            fullWidth
            sx={{ minHeight: 44 }}
          >
            cloudEQ staff sign-in
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
